/* eslint-disable @typescript-eslint/naming-convention */
// helpers
import {
    isUpiCollectPaymentMethod,
    getUpiCollectPaymentParams,
    getUpiIntentPaymentParams,
    getCardPaymentParams,
    getNetBankingPaymentParams,
} from "@payment-client/helpers/OrderHelper";
import { isCardPaymentMethod } from "@payment-client/helpers/OrderHelper";
import { isNetBankingPaymentMethod } from "@payment-client/helpers/PaymentHelper";
import { isInAppUpiPaymentMethod } from "@payment-client/helpers/InAppUpiHelper";
import { InAppUpiPaymentMethod, isPhonePePaymentMethod, isAmazonWallet, isAmazonPayLater } from "@swiggy-private/payment-ui";
import {
    isPaymentMethodUpiAndPaymentCodeUpiIntent,
    isPaymentMethodUpiAndPaymentCodePhonePeUpi,
} from "@payment-client/helpers/UpiHelper";
import { isEmpty } from "@swiggy-private/js-utils";
import { isWalletPaymentMethod } from "@payment-client/helpers/WalletHelper";
import { transformGiftingOrderResponse } from "@payment-client/apis/PublicGiftingOrderService/order-transformer";
// types
import { ENDPOINT } from "@payment-client/apis/Order";
import { PlaceOrderData, makeDownstreamApiCall } from "@swiggy-private/checkout-core";
import { ApiResponse, PHONEPE_PLACE_ORDER_TYPE_KEY } from "@swiggy-private/payment-core";
import { PlaceOrderParams } from "@payment-client/apis/Order";
import {
    GiftingPaymentInfoMethodMetadata,
    AmazonPaymentMethodMeta,
    OrderApiData,
    GiftingOrderResponseData,
    PLACE_ORDER_SOURCE,
    BOOLEAN_AS_STRING,
} from "@swiggy-private/payment-types";

export const publicGiftingPlaceOrder = async (
    apiHost: string,
    params: PlaceOrderParams,
    placeOrderData?: PlaceOrderData,
): Promise<ApiResponse<OrderApiData>> => {
    if (placeOrderData == null || params == null) {
        throw new Error("Place order data is required to make gifting place order");
    }

    let paymentInfoMetaParams: GiftingPaymentInfoMethodMetadata = {};
    const { paymentMethodData: paymentData } = placeOrderData || {};
    const { extraParams = {}, extraArbitaryData = {} } = placeOrderData || {};

    if (isUpiCollectPaymentMethod(paymentData)) {
        paymentInfoMetaParams = getUpiCollectPaymentParams(paymentData, extraParams);
    } else if (
        isPaymentMethodUpiAndPaymentCodeUpiIntent(paymentData) ||
        isPaymentMethodUpiAndPaymentCodePhonePeUpi(paymentData)
    ) {
        paymentInfoMetaParams = getUpiIntentPaymentParams(paymentData);
    } else if (isCardPaymentMethod(paymentData)) {
        paymentInfoMetaParams = {
            card_payment_info: {
                ...getCardPaymentParams(paymentData, extraParams),
                juspay_redirect_url: params.paymentReturnUrl,
            },
        };
    } else if (isNetBankingPaymentMethod(paymentData)) {
        paymentInfoMetaParams = {
            netbanking_payment_info: {
                ...getNetBankingPaymentParams(paymentData),
                juspay_redirect_url: params.paymentReturnUrl,
            },
        };
    } else if (isInAppUpiPaymentMethod(paymentData)) {
        paymentInfoMetaParams = {
            in_app_upi_info: {
                account_reference_id: (paymentData as InAppUpiPaymentMethod)?.meta?.referenceId || "",
                account_type: (paymentData as InAppUpiPaymentMethod)?.meta?.accountType || "",
            },
        };
    } else if (isWalletPaymentMethod(paymentData)) {
        if (isPhonePePaymentMethod(paymentData) && !isEmpty(params.phonepe_payment_type)) {
            paymentInfoMetaParams = {
                phonepe_payment_info: {
                    [PHONEPE_PLACE_ORDER_TYPE_KEY]: params.phonepe_payment_type,
                    web_callback_url: params.paymentReturnUrl,
                },
            };
        } else if (
            isAmazonWallet(paymentData) &&
            !isEmpty(params?.payment_method_meta as AmazonPaymentMethodMeta)
        ) {
            const meta = params?.payment_method_meta as AmazonPaymentMethodMeta;
            paymentInfoMetaParams = {
                amazon_payment_info: {
                    is_enough_balance_in_wallet:
                        meta?.isEnoughBalanceInWallet === BOOLEAN_AS_STRING.TRUE, // gifting expects this as boolean; other BLs expect string
                    redirect_url: params.paymentReturnUrl,
                    meta: meta?.amazon_payment_meta_data ?? "",
                },
            };
        }
    } else if (
        isAmazonPayLater(paymentData?.paymentCode) &&
        !isEmpty(params?.payment_method_meta as AmazonPaymentMethodMeta)
    ) {
        const meta = params?.payment_method_meta as AmazonPaymentMethodMeta;
        paymentInfoMetaParams = {
            amazon_payment_info: {
                is_enough_balance_in_wallet:
                    meta?.isEnoughBalanceInWallet === BOOLEAN_AS_STRING.TRUE,
                redirect_url: params.paymentReturnUrl,
                meta: meta?.amazon_payment_meta_data ?? "",
            },
        };
    }

    const finalPlaceOrderParams = {
        payment_info: {
            transaction_amount: Number(params.paymentAmount),
            payment_method: params.paymentMethod,
            metadata: {
                ...paymentInfoMetaParams,
                selected_split_pay_amounts: params.selected_split_pay_amounts as Record<string, string>,
                twid_rewards_meta: params.twid_rewards_meta as Record<string, string>,
            },
        },
        source: extraArbitaryData?.isTurboPaymentMethod ? PLACE_ORDER_SOURCE.TURBOCHECKOUT : PLACE_ORDER_SOURCE.PAYMENT,
    };

    const backendResponse = await makeDownstreamApiCall(
        {
            apiHost,
            endpoint: ENDPOINT.GIFTING_PLACE_ORDER,
            bodyParams: finalPlaceOrderParams,
            options: { method: "POST" },
        },
        true,
    );

    const { rawResponse, data, ...extraFields } = backendResponse;
    const buildResponse = {
        ...extraFields,
        data,
    } as ApiResponse<GiftingOrderResponseData, unknown>;

    const transformedResponse = transformGiftingOrderResponse(buildResponse, rawResponse);

    // expected response in SDK - { statusCode, data, rawResponse, pendingPaymentParams } = response;
    return transformedResponse;
};
